// Everything the screens draw from, plus each view's filters. Views read this and never fetch on their own.
import type { FilterKey, Item, Job, Pm, Profile, Stage, Task } from '@shared/index';
import { melbourneToday, stageItems, stagesOf } from '@shared/index';

export const store = {
  jobs: [] as Job[],
  stages: [] as Stage[],
  tasks: [] as Task[],
  pms: [] as Pm[],
  profile: null as Profile | null,
  canWrite: false,
  loaded: false,
  demo: false,
};

/** Melbourne's calendar date, read fresh each time so a tab left open overnight rolls over. */
export const today = () => melbourneToday();

const [ty, tm] = melbourneToday().split('-').map(Number);

export const ui = {
  /** Jobs tab */
  pm: '', q: '', filter: '' as FilterKey, open: new Set<string>(), view: 'active' as 'active' | 'done',
  /** Overview "All jobs" table */
  ov: { pm: '', ty: '', q: '', k: 'date', dir: -1 },
  /** Calendar (m is 0-based) */
  cal: { pm: '', y: ty, m: tm - 1 },
  /** To do */
  tdo: { pm: '' },
  /** Open "next steps" groups survive re-renders */
  stageOpen: new Set<string>(),
};

/** Start every PM filter on the signed-in PM's own jobs. */
export function defaultPm(pm: string) {
  ui.pm = pm; ui.ov.pm = pm; ui.cal.pm = pm; ui.tdo.pm = pm;
}

export const jobItems = (): Item[] => store.jobs as unknown as Item[];
export const stagesOfJob = (quote: string): Item[] => stagesOf(store.stages, store.jobs, quote);
export const allStageItems = (): Item[] => stageItems(store.stages, store.jobs);
export const isStageId = (id: string) => store.stages.some((x) => x.id === id);
export const activePms = () => store.pms.filter((p) => p.active).map((p) => p.name);

// ---------- Applying changes (local saves and realtime events) ----------

type Table = 'jobs' | 'stages' | 'tasks' | 'pms';
const KEY: Record<Table, string> = { jobs: 'quote', stages: 'id', tasks: 'id', pms: 'name' };

function list(t: Table): Record<string, unknown>[] {
  return store[t] as unknown as Record<string, unknown>[];
}

/** Insert or replace a row by its key. Numeric columns come back from Postgres as numbers or strings; keep numbers. */
export function upsertRow(t: Table, row: Record<string, unknown>) {
  const k = KEY[t];
  if (t === 'jobs') for (const c of ['quoted', 'invoiced', 'remaining']) if (row[c] !== undefined) row[c] = Number(row[c]);
  const L = list(t);
  const i = L.findIndex((x) => x[k] === row[k]);
  if (i >= 0) L[i] = { ...L[i], ...row }; else L.push(row);
}

export function removeRow(t: Table, key: string) {
  const L = list(t);
  const i = L.findIndex((x) => x[KEY[t]] === key);
  if (i >= 0) L.splice(i, 1);
  if (t === 'jobs') {
    // the database removes these too; mirror it so nothing points at a missing job
    const gone = new Set(store.stages.filter((s) => s.parent_quote === key).map((s) => s.id));
    store.stages = store.stages.filter((s) => s.parent_quote !== key);
    store.tasks = store.tasks.filter((x) => x.quote !== key && !gone.has(x.quote || ''));
  }
  if (t === 'stages') store.tasks = store.tasks.filter((x) => x.quote !== key);
}

export function findRow(t: Table, key: string): Record<string, unknown> | undefined {
  return list(t).find((x) => x[KEY[t]] === key);
}
